'use client';

import { memo, useCallback, useMemo, useState } from 'react';

import Markdown from 'markdown-to-jsx';

import { cn } from '../../lib/utils';

// ---------------------------------------------------------------------------
// SafeImage — referrer bypass + hide broken images
// ---------------------------------------------------------------------------

function SafeImage(props: React.ImgHTMLAttributes<HTMLImageElement>) {
  const [hidden, setHidden] = useState(false);

  const onError = useCallback(() => setHidden(true), []);

  if (hidden) return null;

  return (
    <img
      {...props}
      referrerPolicy="no-referrer"
      loading="lazy"
      onError={onError}
    />
  );
}

function SafeLink(
  props: React.AnchorHTMLAttributes<HTMLAnchorElement>,
) {
  return (
    <a {...props} target="_blank" rel="noopener noreferrer" />
  );
}

/**
 * Link component that resolves target dynamically via a callback.
 * Used when the widget needs same-origin → _self, cross-origin → _blank.
 */
function createDynamicLink(
  resolveFn: (href: string) => string,
) {
  return function DynamicLink(
    props: React.AnchorHTMLAttributes<HTMLAnchorElement>,
  ) {
    const target = resolveFn(props.href || '');
    return <a {...props} target={target} rel="noopener noreferrer" />;
  };
}

// ---------------------------------------------------------------------------
// ResponsiveTable — converts wide tables to vertical card layout
// When a table would overflow its container, each row becomes a card
// with label-value pairs stacked vertically.
// ---------------------------------------------------------------------------

function ResponsiveTable(props: React.TableHTMLAttributes<HTMLTableElement> & { children?: React.ReactNode }) {
  const { children, ...rest } = props;

  // Extract headers and body rows from children
  const headers: string[] = [];
  const rows: React.ReactNode[][] = [];

  const childArray = Array.isArray(children) ? children : [children];
  for (const child of childArray) {
    if (!child || typeof child !== 'object' || !('props' in child)) continue;
    const section = child as React.ReactElement<{ children?: React.ReactNode }>;
    const sectionChildren = Array.isArray(section.props.children)
      ? section.props.children
      : [section.props.children];

    // thead
    if (section.type === 'thead') {
      for (const tr of sectionChildren) {
        if (!tr || typeof tr !== 'object' || !('props' in tr)) continue;
        const rowChildren = (tr as React.ReactElement<{ children?: React.ReactNode }>).props.children;
        const cells = Array.isArray(rowChildren) ? rowChildren : [rowChildren];
        for (const th of cells) {
          if (!th) continue;
          const text = typeof th === 'string' ? th
            : typeof th === 'object' && 'props' in th
              ? extractText(th)
              : '';
          headers.push(text);
        }
      }
    }

    // tbody
    if (section.type === 'tbody') {
      for (const tr of sectionChildren) {
        if (!tr || typeof tr !== 'object' || !('props' in tr)) continue;
        const rowChildren = (tr as React.ReactElement<{ children?: React.ReactNode }>).props.children;
        const cells = Array.isArray(rowChildren) ? rowChildren : [rowChildren];
        rows.push(cells);
      }
    }
  }

  // 2-column tables (Detail/Info, Course/Price): render as ONE card with
  // each row as a label-value pair. The first column is the label, second
  // is the value. This produces a clean summary card for enrolment details.
  if (headers.length === 2 && rows.length > 0) {
    return (
      <div
        className="responsive-table-card"
        style={{
          borderRadius: '0.5rem',
          border: '1px solid rgba(128,128,128,0.2)',
          padding: '0.5rem 0.75rem',
        }}
      >
        {rows.map((cells, rowIdx) => {
          const label = cells[0];
          const value = cells[1];
          if (!label && !value) return null;
          return (
            <div
              key={rowIdx}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                padding: '0.35rem 0',
                borderBottom: rowIdx < rows.length - 1 ? '1px solid rgba(128,128,128,0.1)' : 'none',
              }}
            >
              <span style={{ fontWeight: 600, opacity: 0.7, marginRight: '1rem', whiteSpace: 'nowrap' }}>
                {typeof label === 'object' && label !== null && 'props' in label ? (label as React.ReactElement<{ children?: React.ReactNode }>).props.children : label}
              </span>
              <span style={{ textAlign: 'right' }}>
                {typeof value === 'object' && value !== null && 'props' in value ? (value as React.ReactElement<{ children?: React.ReactNode }>).props.children : value}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  // 3+ column tables: per-row card layout (each row becomes its own card
  // with header labels). Good for course listings, date/spots tables.
  if (headers.length >= 3 && rows.length > 0) {
    return (
      <div className="responsive-table-cards" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {rows.map((cells, rowIdx) => (
          <div
            key={rowIdx}
            style={{
              borderRadius: '0.5rem',
              border: '1px solid rgba(128,128,128,0.2)',
              padding: '0.5rem 0.75rem',
            }}
          >
            {cells.map((cell, colIdx) => {
              if (!cell) return null;
              return (
                <div
                  key={colIdx}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    padding: '0.2rem 0',
                    borderBottom: colIdx < cells.length - 1 ? '1px solid rgba(128,128,128,0.1)' : 'none',
                  }}
                >
                  <span style={{ fontWeight: 600, opacity: 0.7, marginRight: '1rem', whiteSpace: 'nowrap' }}>
                    {headers[colIdx] || ''}
                  </span>
                  <span style={{ textAlign: 'right' }}>
                    {typeof cell === 'object' && 'props' in cell ? (cell as React.ReactElement<{ children?: React.ReactNode }>).props.children : cell}
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  }

  // Fallback for single-column or headerless tables
  return <table {...rest}>{children}</table>;
}

function extractText(node: unknown): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (!node || typeof node !== 'object' || !('props' in node)) return '';
  const el = node as React.ReactElement<{ children?: React.ReactNode }>;
  if (typeof el.props.children === 'string') return el.props.children;
  if (Array.isArray(el.props.children)) return el.props.children.map(extractText).join('');
  return extractText(el.props.children);
}

const markdownOverrides = {
  overrides: {
    img: {
      component: SafeImage,
    },
    a: {
      component: SafeLink,
    },
    table: {
      component: ResponsiveTable,
    },
  },
};

// ---------------------------------------------------------------------------
// Table normalizer — rejoin & rebuild fragmented markdown tables
// ---------------------------------------------------------------------------

function isTableLine(line: string): boolean {
  return line.trim().startsWith('|');
}

function parseCells(line: string): string[] {
  let trimmed = line.trim();
  if (trimmed.startsWith('|')) trimmed = trimmed.slice(1);
  if (trimmed.endsWith('|')) trimmed = trimmed.slice(0, -1);
  return trimmed.split('|').map((c) => c.trim());
}

function isSeparator(cells: string[]): boolean {
  return (
    cells.length > 0 && cells.every((c) => /^:?-{2,}:?$/.test(c) || c === '')
  );
}

/**
 * Step 1: Rejoin lines broken across chunk boundaries.
 *
 * Fixes two patterns:
 * a) `[Company](url) |` on its own line (separated by blank lines from the
 *    table row it belongs to) — merge back into the last table row.
 * b) `| time | location | ...` rows that are really the second half of a
 *    row split mid-cell — detected by the previous row ending without `|`.
 */
function rejoinBrokenRows(text: string): string {
  const lines = text.split('\n');
  const result: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i]!.trim();

    // Skip standalone `|` lines (empty table cell markers from chunk edges)
    if (trimmed === '|') continue;

    // Pattern A: continuation fragment like `[Company](url) |`
    // Not a table row (no leading |), but ends with | — it's a broken link
    if (
      !trimmed.startsWith('|') &&
      trimmed.endsWith('|') &&
      /^\[/.test(trimmed)
    ) {
      // Find the last table row in result (looking past blank lines)
      for (let j = result.length - 1; j >= 0; j--) {
        if (result[j]!.trim() === '') continue;
        if (isTableLine(result[j]!)) {
          result[j] += ' ' + trimmed;
          break;
        }
        break; // hit non-table, non-blank — stop
      }
      continue;
    }

    // Pattern B: Previous table row ends without `|` and current starts with `|`
    // → The current row is a shifted duplicate (offset by 1 column, missing date)
    //   or a genuine continuation. Detect & smart-merge.
    if (
      result.length > 0 &&
      isTableLine(lines[i]!)
    ) {
      // Find last non-blank line in result
      let lastIdx = result.length - 1;
      while (lastIdx >= 0 && result[lastIdx]!.trim() === '') lastIdx--;

      if (
        lastIdx >= 0 &&
        isTableLine(result[lastIdx]!) &&
        !result[lastIdx]!.trim().endsWith('|')
      ) {
        const prevCells = parseCells(result[lastIdx]!);
        const currCells = parseCells(lines[i]!);

        // Check if current is shifted (offset by 1 column): prev[1]==curr[0], prev[2]==curr[1]
        const isShifted =
          prevCells.length >= 3 &&
          currCells.length >= 2 &&
          prevCells[1] === currCells[0] &&
          prevCells[2] === currCells[1];

        if (isShifted) {
          // Merge: date from prev + complete data from curr
          const merged = [prevCells[0]!, ...currCells];
          result[lastIdx] = '| ' + merged.join(' | ') + ' |';
        } else {
          // Genuine continuation — concatenate
          result[lastIdx] += ' ' + trimmed;
        }
        // Remove any blank lines we skipped over
        while (result.length - 1 > lastIdx) result.pop();
        continue;
      }
    }

    result.push(lines[i]!);
  }

  return result.join('\n');
}

/**
 * Step 2: Merge all table fragments in a region into one clean table.
 */
function rebuildTable(tableLines: string[]): string[] {
  // Flatten any multi-line entries into single rows
  const flattened: string[] = [];
  for (const line of tableLines) {
    if (line.includes('\n')) {
      flattened.push(line.split('\n').map((l) => l.trim()).join(' '));
    } else {
      flattened.push(line);
    }
  }

  const allParsed = flattened.map(parseCells);

  // Classify rows
  const separatorIndices = new Set<number>();
  const headerIndices = new Set<number>();

  for (let i = 0; i < allParsed.length; i++) {
    if (isSeparator(allParsed[i]!)) {
      separatorIndices.add(i);
      if (i > 0 && !separatorIndices.has(i - 1)) {
        headerIndices.add(i - 1);
      }
    }
  }

  const headers: string[][] = [];
  const dataRows: string[][] = [];

  for (let i = 0; i < allParsed.length; i++) {
    if (separatorIndices.has(i)) continue;
    const row = [...allParsed[i]!];
    // Trim trailing empty cells
    while (row.length > 1 && row[row.length - 1] === '') row.pop();

    // Skip tiny fragment rows (1-2 short cells) when table is wide
    if (row.length <= 2 && row.every((c) => c.length < 30)) {
      if (allParsed.some((r) => r.length > 4)) continue;
    }

    if (headerIndices.has(i)) {
      headers.push(row);
    } else {
      dataRows.push(row);
    }
  }

  let header = headers.length > 0 ? [...headers[0]!] : null;

  // Max columns from all rows
  let maxCols = header ? header.length : 0;
  for (const row of dataRows) {
    if (row.length > maxCols) maxCols = row.length;
  }

  if (maxCols === 0) return tableLines;

  // Pad header
  if (header) {
    while (header.length < maxCols) header.push('');
    header = header.slice(0, maxCols);
  } else {
    header = Array.from({ length: maxCols }, () => '');
  }

  // Collect unique, non-duplicate data rows.
  // When two rows have the same first column (e.g. same date),
  // prefer the one with more filled cells.
  const byFirstCol = new Map<string, string[]>();
  const ordered: string[] = []; // track insertion order of first-col keys

  for (const row of dataRows) {
    const padded = [...row];
    while (padded.length < maxCols) padded.push('');
    const trimmedRow = padded.slice(0, maxCols);

    // Skip rows matching the header
    if (trimmedRow.every((c, ci) => c === header![ci])) continue;

    // Skip rows that are clearly shifted (first cell looks like a time, not a date)
    // — these are chunk-boundary duplicates missing the date column
    if (/^\d{1,2}:\d{2}/.test(trimmedRow[0]!)) continue;

    const key = trimmedRow[0]!; // first column (Date)
    const existing = byFirstCol.get(key);

    if (existing) {
      // Keep whichever has more filled cells
      const existingFilled = existing.filter((c) => c.length > 0).length;
      const newFilled = trimmedRow.filter((c) => c.length > 0).length;
      if (newFilled > existingFilled) {
        byFirstCol.set(key, trimmedRow);
      }
    } else {
      byFirstCol.set(key, trimmedRow);
      ordered.push(key);
    }
  }

  // Build final table
  const result: string[] = [];
  result.push('| ' + header.join(' | ') + ' |');
  result.push(
    '| ' +
      Array.from({ length: maxCols }, () => '---').join(' | ') +
      ' |',
  );
  for (const key of ordered) {
    const row = byFirstCol.get(key)!;
    result.push('| ' + row.join(' | ') + ' |');
  }

  return result;
}

/**
 * Step 3: Find table regions and normalize them.
 */
function normalizeMarkdownTables(text: string): string {
  text = rejoinBrokenRows(text);

  const lines = text.split('\n');
  const result: string[] = [];
  let i = 0;

  while (i < lines.length) {
    if (isTableLine(lines[i]!)) {
      const tableLines: string[] = [];

      while (i < lines.length) {
        const current = lines[i]!;

        if (isTableLine(current)) {
          tableLines.push(current);
          i++;
        } else if (current.trim() === '') {
          // Look ahead for more table rows
          let hasMore = false;
          for (let j = i + 1; j < Math.min(i + 4, lines.length); j++) {
            if (isTableLine(lines[j]!)) {
              hasMore = true;
              break;
            }
          }
          if (hasMore) {
            i++;
          } else {
            break;
          }
        } else {
          break;
        }
      }

      if (tableLines.length >= 2) {
        result.push(...rebuildTable(tableLines));
      } else {
        result.push(...tableLines);
      }
    } else {
      result.push(lines[i]!);
      i++;
    }
  }

  return result.join('\n');
}

// ---------------------------------------------------------------------------
// Hard-break normalizer — single newlines inside plain text become <br>
// ---------------------------------------------------------------------------

/**
 * A line that already carries Markdown block structure. Appending a hard
 * break to one of these (or to the line before it) changes list / heading /
 * table semantics in markdown-to-jsx instead of just adding a line break:
 * a trailing "  " on a list-item line or on the blank line after a list
 * turns a tight list into a loose one and swallows the following paragraph
 * into the last item (seen on the Platform DPA, section 3).
 */
function isBlockStructureLine(line: string): boolean {
  const t = line.trim();
  if (t === '') return true;
  if (/^([-*+]|\d+[.)])\s/.test(t)) return true; // list item
  if (/^#{1,6}\s/.test(t)) return true; // heading
  if (t.startsWith('|')) return true; // table row
  if (t.startsWith('>')) return true; // blockquote
  if (/^([-*_])( ?\1){2,}$/.test(t)) return true; // thematic break
  return false;
}

/**
 * Convert single newlines to Markdown hard breaks (two trailing spaces) so
 * that \n renders as an actual line break in chat messages. Only applies
 * between two consecutive plain-text lines: block-structure lines (lists,
 * headings, tables, blank lines, fences) and the line right before one are
 * left untouched so the document structure is preserved. Fenced code blocks
 * are skipped entirely.
 */
function addHardBreaks(text: string): string {
  const lines = text.split('\n');
  let inCodeBlock = false;
  return lines
    .map((line, i) => {
      if (line.trim().startsWith('```')) inCodeBlock = !inCodeBlock;
      if (inCodeBlock) return line;
      if (isBlockStructureLine(line)) return line;
      const next = lines[i + 1];
      if (next === undefined || next.trim().startsWith('```')) return line;
      if (isBlockStructureLine(next)) return line;
      return line.replace(/(?<! {2})$/, '  ');
    })
    .join('\n');
}

// ---------------------------------------------------------------------------
// MarkdownRenderer
// ---------------------------------------------------------------------------

const MemoizedReactMarkdown = memo(
  Markdown,
  (prevProps, nextProps) =>
    prevProps.children === nextProps.children &&
    prevProps.className === nextProps.className,
);

export function MarkdownRenderer(
  props: React.PropsWithChildren<{
    className?: string;
    children: string;
    /** Resolve link target dynamically. When provided, each <a> tag calls
     *  this with the href and uses the returned value as the target attribute.
     *  Omit to default all links to _blank. */
    linkTarget?: (href: string) => string;
  }>,
) {
  const normalized = useMemo(
    () => {
      const text = normalizeMarkdownTables(props.children);
      return addHardBreaks(text);
    },
    [props.children],
  );

  // Build overrides with dynamic link target when provided
  const options = useMemo(() => {
    if (!props.linkTarget) return markdownOverrides;
    return {
      overrides: {
        img: { component: SafeImage },
        a: { component: createDynamicLink(props.linkTarget) },
        table: { component: ResponsiveTable },
      },
    };
  }, [props.linkTarget]);

  return (
    <MemoizedReactMarkdown
      className={cn(props.className, 'markdoc')}
      options={options}
    >
      {normalized}
    </MemoizedReactMarkdown>
  );
}
